import React, { useEffect } from 'react';
import { styled } from '@uhg-abyss/web/tools/styled';
import { useTIN } from '@src/state/taxInformation';
import { useOnloading } from '@src/state/landing';
import { PageLayout } from '@src/common/PageLayout';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { UHCProviderLink } from '@src/common/UHCProviderLink';
import { event as adobeTaggingEvent } from '@uhg-abyss/web/tools/event';
import { useUser } from '@src/state/user';
import { getOrgType } from '@src/common/helpers';
import { UHCLink } from '@src/common/UHCLink';
import { config } from '@uhg-abyss/web/tools/config';
import { IconBrand } from '@uhg-abyss/web/ui/IconBrand';
import { LagoonContext } from '@src/AppContext/LagoonContext';

const Container = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
});

const MiddleColumn = styled('div', {
  margin: 'auto',
  width: '675px',
});

const Layout = styled('div', {
  margin: 'auto',
  borderRadius: '3px',
  border: '1px solid rgb(219, 219, 219)',
  background: '$white',
  flex: 1,
  paddingLeft: 20,
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  alignSelf: 'center',
  marginTop: '5%',
});

const LayoutHeader = styled('h2', {
  fontSize: '24px',
  fontWeight: 600,
  lineHeight: '32px',
  color: '$primary1',
  width: '80%',
});

const LayoutInstruction = styled('p', {
  fontWeight: 500,
  paddingTop: '10px',
  fontSize: '16px',
});

const LayoutIcon = styled('div', {
  padding: '10px',
});

const Contact = styled('text', {
  fontFamily: '$primary',
  color: '#196ECF',
});

const ButtonView = styled('div', {
  display: 'flex',
  margin: 'auto',
  marginTop: '15px',
  flexDirection: 'row',
  justifyContent: 'space-between',
});

const MiddleContent = () => {
  const { isAPIMarketPlaceUser } = useUser.state();

  return (
    <Layout>
      <LayoutIcon>
        <IconBrand
          icon="alert"
          style={{
            height: '70px',
            width: '70px',
            marginTop: '15px',
            marginBottom: '20px',
          }}
          variant="twotone"
        />
      </LayoutIcon>
      <React.Fragment>
        <LayoutHeader>
          We can't verify your organization's identity based on the information
          you've entered.
        </LayoutHeader>
        <LayoutInstruction>
          {isAPIMarketPlaceUser ? (
            <React.Fragment>
              Please contact us at <Contact>apiconsultant@uhc.com</Contact>.
            </React.Fragment>
          ) : (
            <React.Fragment>
              For additional assistance, please contact our UnitedHealthcare Web
              Support team by clicking the chat icon on the bottom right of this
              screen. Support is also available by calling 866-842-3278, option
              1.
            </React.Fragment>
          )}
        </LayoutInstruction>
      </React.Fragment>
    </Layout>
  );
};

export const LockMessageComponent = () => {
  const { currentFlow } = useOnloading.state();
  const { TINNumber, TINInfo } = useTIN.state();
  const { userUUID, isAPIMarketPlaceUser } = useUser.state();

  const lagoonContext = React.useContext(LagoonContext);
  const featureToggles = lagoonContext.getTable('feature-toggles');
  const uiMessages = lagoonContext.getTable('ui-messaging');

  useEffect(() => {
    if (featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active) {
      const orgType = getOrgType(currentFlow);
      const section1 = 'paa registration';
      const section2 = 'user lockedout';
      const pageName = `digitalsecurity:${section1}:${section2}`;

      adobeTaggingEvent('ON_PAGE_LOAD', {
        pageName,
        orgType,
        organization: TINInfo?.corpName,
        uuid: userUUID,
        tin: TINNumber,
        section1,
        section2,
        section3: '',
      });
    }
  }, []);

  return (
    <PageLayout style={{ paddingTop: '50px' }}>
      <Container>
        <MiddleColumn>
          <MiddleContent />
          <Flex.Content>
            <ButtonView>
              {isAPIMarketPlaceUser ? (
                <UHCLink
                  label="Return to API Marketplace"
                  url={
                    uiMessages.getRow('API_MARKETPLACE_WELCOME_URL')?.message ||
                    config('API_MARKETPLACE_WELCOME_URL')
                  }
                  sameTab
                />
              ) : (
                <UHCProviderLink
                  style={{
                    width: '100%',
                    display: 'inline-flex',
                    marginTop: '20px',
                    flexDirection: 'row',
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}
                />
              )}
            </ButtonView>
          </Flex.Content>
        </MiddleColumn>
      </Container>
    </PageLayout>
  );
};
